import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeftRight, Calculator, Lock, LockOpen, MinusCircle, Plus, PlusCircle, ScrollText } from "lucide-react";
import { toast } from "sonner";
import usePoll from "@/hooks/usePoll";
import { useAuth } from "@/context/AuthContext";
import { api, dt, errMsg, money } from "@/lib/api";
import { Empty, ErrorBox, Loading, Money, PageHeader } from "@/components/common";
import { CountDialog, CreateTreasuryDialog, MoveDialog, TransferDialog } from "@/components/TreasuryForms";

function TreasuryCard({ t, act, canOp }) {
  const lc = t.last_count;
  const row = (k, v, cls = "") => <div className="flex justify-between text-sm"><span className="text-slate-500">{k}</span><b className={`num ${cls}`}>{v}</b></div>;
  return (
    <div className="k-card p-5 space-y-3" data-testid={`treasury-card-${t.code}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1"><div className="font-display font-extrabold text-lg">{t.name}</div><div className="text-xs text-slate-500">{t.kind === "main" ? "خزينة المؤسسة" : t.user_name || "خزينة عامة"}{!t.active && " • معطلة"}</div></div>
        <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${t.status === "open" ? "bg-emerald-50 text-emerald-800" : "bg-slate-100 text-slate-600"}`} data-testid={`treasury-status-${t.code}`}>{t.status === "open" ? "مفتوحة" : "مغلقة"}</span>
      </div>
      <div className="text-3xl font-black num text-[#1A4D2E]" data-testid={`treasury-balance-${t.code}`}>{money(t.balance)}</div>
      <div className="space-y-1">
        {row("الرصيد الافتتاحي", money(t.opening_balance))}
        {row("مجموع المداخيل", money(t.income_total), "text-emerald-700")}
        {row("مجموع المخارج", money(t.expense_total), "text-rose-700")}
        {row("تحويلات واردة / صادرة", `${money(t.transfer_in)} / ${money(t.transfer_out)}`)}
        {row("الرصيد الفعلي عند آخر جرد", lc ? `${money(lc.counted)} (${lc.diff === 0 ? "مطابق" : `فرق ${money(lc.diff)}`})` : "—", lc?.diff ? "text-amber-700" : "")}
      </div>
      <div className="flex flex-wrap gap-2 pt-1">
        <Link className="k-btn-outline k-btn-sm" to={`/treasuries/${t.id}`} data-testid={`treasury-statement-${t.code}`}><ScrollText className="w-4 h-4" /> كشف الحساب</Link>
        {canOp && t.active && <>
          {t.status === "open" ? <button className="k-btn-outline k-btn-sm" onClick={() => act("close", t)} data-testid={`treasury-close-${t.code}`}><Lock className="w-4 h-4" /> إغلاق</button>
            : <button className="k-btn-outline k-btn-sm" onClick={() => act("open", t)} data-testid={`treasury-open-${t.code}`}><LockOpen className="w-4 h-4" /> فتح</button>}
          <button className="k-btn-outline k-btn-sm" onClick={() => act("transfer", t)} data-testid={`treasury-transfer-${t.code}`}><ArrowLeftRight className="w-4 h-4" /> تحويل</button>
          <button className="k-btn-outline k-btn-sm" onClick={() => act("count", t)} data-testid={`treasury-count-${t.code}`}><Calculator className="w-4 h-4" /> جرد</button>
          {act.canMoves && <>
            <button className="k-btn-outline k-btn-sm text-rose-700" onClick={() => act("withdrawal", t)} data-testid={`treasury-withdraw-${t.code}`}><MinusCircle className="w-4 h-4" /> سحب / مخرج</button>
            <button className="k-btn-outline k-btn-sm text-emerald-700" onClick={() => act("other_income", t)} data-testid={`treasury-income-${t.code}`}><PlusCircle className="w-4 h-4" /> مدخول</button>
          </>}
        </>}
      </div>
    </div>
  );
}

export default function Treasuries() {
  const { user, can } = useAuth();
  const { data, error, reload } = usePoll("/treasuries", null, 20000);
  const [dlg, setDlg] = useState(null);
  const [targets, setTargets] = useState([]);
  const [users, setUsers] = useState([]);
  const [check, setCheck] = useState(null);
  useEffect(() => {
    api.get("/treasuries/targets").then((r) => setTargets(r.data)).catch(() => {});
    if (can("manage_treasuries")) api.get("/users").then((r) => setUsers(r.data.filter((u) => u.active))).catch(() => {});
    if (can("view_all_treasuries")) api.get("/treasuries/check").then((r) => setCheck(r.data)).catch(() => {});
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (a, t) => {
    if (a === "open" || a === "close") {
      try { await api.post(`/treasuries/${t.id}/${a}`, {}); toast.success(a === "open" ? "تم فتح الخزينة" : "تم إغلاق الخزينة"); reload(); } catch (e) { toast.error(errMsg(e)); }
    } else setDlg({ a, t });
  };
  act.canMoves = can("manage_expenses");
  const close = () => setDlg(null);
  const total = (data || []).reduce((s, t) => s + t.balance, 0);
  return (
    <div className="space-y-4">
      <PageHeader title="الخزائن" subtitle="أرصدة الخزائن، الفتح والإغلاق اليومي، التحويلات والجرد">
        {can("manage_treasuries") && <button className="k-btn-primary" onClick={() => setDlg({ a: "create" })} data-testid="add-treasury-btn"><Plus className="w-5 h-5" /> خزينة جديدة</button>}
      </PageHeader>
      <ErrorBox text={error} />
      {check && <div className={`k-card p-3 text-sm font-bold ${check.all_ok ? "text-emerald-700" : "text-rose-700 border-rose-300"}`} data-testid="ledger-check">
        {check.all_ok ? "المطابقة سليمة: كل رصيد يساوي مجموع حركاته الموثقة" : `تنبيه: اختلاف بين الرصيد والحركات في ${check.treasuries.filter((x) => !x.ok).map((x) => x.name).join("، ")}`}</div>}
      {!data ? <Loading /> : !data.length ? <div className="k-card"><Empty text="لا توجد خزينة مسندة إليك" /></div> : (
        <>
          {data.length > 1 && <div className="k-card p-4 font-bold">مجموع أرصدة الخزائن المعروضة: <Money v={total} className="text-xl" /></div>}
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {data.map((t) => <TreasuryCard key={t.id} t={t} act={act} canOp={t.user_id === user.id || can("manage_treasuries")} />)}
          </div>
          <div className="text-xs text-slate-500">آخر تحديث {dt(new Date().toISOString())}</div>
        </>
      )}
      {dlg?.a === "create" && <CreateTreasuryDialog users={users} onOpenChange={close} done={reload} />}
      {(dlg?.a === "withdrawal" || dlg?.a === "other_income") && <MoveDialog t={dlg.t} kind={dlg.a} onOpenChange={close} done={reload} />}
      {dlg?.a === "transfer" && <TransferDialog t={dlg.t} targets={targets} onOpenChange={close} done={reload} />}
      {dlg?.a === "count" && <CountDialog t={dlg.t} onOpenChange={close} done={reload} />}
    </div>
  );
}
